import {
	CoreBackButton,
	CoreButton,
	CoreCard,
	CoreChip,
	CoreDatePicker,
	CoreIconButton,
	CoreMenuItem,
	CoreModal,
	CoreRadioGroup,
	CoreSearchField,
	CoreSelect,
	CoreTable,
	CoreTableColumn,
	CoreTableRow,
	CoreTextField,
	coreToast,
	RadioOption,
} from "@verity/components/core";
import i18n from "../../i18n";
import { useMonthPicker } from "../../helpers/use-month-picker";
import { Box, Grid, IconButton, ListItemText, Skeleton, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { ExportReportInfo, ExportReportRadioGroupProps, ExportType, FormatType, YearSelection } from "./report-types";
import React, { useCallback, useContext, useEffect, useId, useMemo, useState } from "react";
import { PortalControllerContext, usePortalStateContext } from "../../portal/context";
import { ReportsExportedEvent } from "@yujacorp/verity-api-contract/schemas/zod";
import { emitAnalytics, instituteSubject } from "../../helpers/analytics";
import styles from "../reports.module.css";
import {
	exportReport,
	fetchDistinctYearsForReport,
	getEmailError,
	getExportModalDropDownLabel,
	getFlagSeverity,
	getReportData,
} from "./helpers";
import ArrowRight from "../../images/reports/arrowRight.svg?react";
import ExportIcon from "../../images/reports/exportIcon.svg?react";
import NoResultImage from "../../images/reports/table-no-results-available.svg?react";
import NoEmailData from "../../images/reports/no-email-data.svg?react";
import AddResultsImage from "../../images/reports/addResults.svg?react";
import ClockIcon from "../../home/assets/clock.svg?react";
import CalendarIcon from "../../portal/components/interview-hub/InterviewResults/assets/calendar.svg?react";
import CaretRight from "../../images/caret-right.svg?react";
import CaretDown from "../../images/portal/caret-down.svg?react";
import SelectSubAccount from "../../images/select-sub-account.svg?react";
import SelectCourse from "../../images/select-course.svg?react";
import SelectAssessment from "../../images/select-assessment.svg?react";
import Refresh from "../../images/refresh.svg?react";
import type {
	AssessmentTableEntry,
	CourseTableEntry,
	FlagTypeBreakdown,
	ReportType,
	TestTakerTableEntry,
} from "@verity/api-client";
import {
	ANOMALY_TO_DESCRIPTION,
	ANOMALY_TO_EVENT_NAME,
	AnomalyType,
} from "../../portal/components/settings/flag-level-config";
import { TestTakerResultModal } from "./TestTakerResultModal";
import apiClient from "../../helpers/api-client";
import { getScoreColors } from "./IntegrityScoreBreakdown";
import XIcon from "../../images/reports/x-icon.svg?react";
import type { ReportOverviewProps } from "./charts";
import {
	getSetupStepDetails,
	getSetupTextByLMS,
	QuickSetupHighlight,
	SETUP_GUIDE_ATTRIBUTES,
	useScrollIntoView,
} from "@verity/components";
export const badgeStyle = {
	borderRadius: "16px",
	padding: "0px 4px",
	marginLeft: "auto",
	minWidth: "fit-content",
	width: "36px",
	minHeight: "22px",
	fontSize: "14px",
	lineHeight: "1.6",
	fontWeight: "500",
	color: "#575757",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	overflow: "hidden",
	"& .MuiChip-label": {
		whiteSpace: "nowrap",
	},
};

export function FlagLevelBadge({ flagLevel }: { flagLevel: number }) {
	const roundedFlagLevel = Math.round(flagLevel ?? 0);
	const flagLabel = getFlagSeverity(flagLevel);
	const flagColor =
		roundedFlagLevel <= 0
			? "#E6E6E6"
			: roundedFlagLevel <= 1
				? "#DBE7E1"
				: roundedFlagLevel <= 2
					? "#FAEFDE"
					: "#F8DCDB";
	return (
		<CoreChip chipColor={flagColor} sx={{ ...badgeStyle, width: "80px", height: "24px" }}>
			{flagLabel}
		</CoreChip>
	);
}

/** Saturated (non-pastel) severity colors for use on charts/bars, where FlagLevelBadge's pastel chip colors are too light to read. Keyed by flagLevel (0=none…3=severe). */
export const SEVERITY_COLORS: Record<number, string> = {
	0: "#98A2B3",
	1: "#12B76A",
	2: "#F79009",
	3: "#F04438",
};

export function IntegrityLevelBadge({ integrityLevel }: { integrityLevel: number | null }) {
	let chipColor = "#F3F4F6";
	let borderColor = "#D1D5DB";
	let textColor = "#262626";
	let displayValue = "N/A";

	if (integrityLevel !== null && integrityLevel !== undefined) {
		const roundedIntegrityLevel = Math.round(integrityLevel);
		displayValue = `${roundedIntegrityLevel}%`;

		if (roundedIntegrityLevel > 70) {
			chipColor = "#E3F8E6";
			textColor = "#03692B";
			borderColor = "#C2EFC9";
		} else if (roundedIntegrityLevel >= 30) {
			chipColor = "#FFF1EB";
			textColor = "#CB3701";
			borderColor = "#FFD4C4";
		} else {
			chipColor = "#FFEFEF";
			textColor = "#9F0712";
			borderColor = "#FAC8C8";
		}
	}

	return (
		<CoreChip
			chipColor={chipColor}
			sx={{
				...badgeStyle,
				width: "80px",
				height: "24px",
				borderColor: borderColor,
				borderStyle: "solid",
				borderWidth: "1px",
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				textAlign: "center",
			}}
		>
			<Typography
				sx={{
					fontWeight: 400,
					fontSize: "14px",
					lineHeight: "20px",
					letterSpacing: "0%",
					color: textColor,
				}}
			>
				{displayValue}
			</Typography>
		</CoreChip>
	);
}

export const NoResults = ({
	text,
	add,
	emptyEmail,
	description,
}: {
	text: string;
	add?: boolean;
	emptyEmail?: boolean;
	description?: string;
}) => (
	<Box
		display="flex"
		justifyContent="center"
		alignItems="center"
		height="100%"
		width="100%"
		flexDirection="column"
		gap="16px"
	>
		{emptyEmail ? (
			<NoEmailData aria-hidden="true" />
		) : add ? (
			<AddResultsImage aria-hidden="true" />
		) : (
			<NoResultImage aria-hidden="true" />
		)}
		{!description && (
			<Typography fontWeight={500} fontSize={"15px"} textAlign="center">
				{text}
			</Typography>
		)}
		{description && (
			<>
				<Typography fontWeight={800} fontSize={"20px"} textAlign="center">
					{text}
				</Typography>
				<Typography fontWeight={500} fontSize={"15px"} textAlign="center">
					{description}
				</Typography>
			</>
		)}
	</Box>
);

function ExportYearDropdown({
	selectedYear,
	onChange,
	availableYears,
}: {
	selectedYear: YearSelection;
	onChange: (value: YearSelection) => void;
	availableYears: number[];
}) {
	const dateDropdownId = useId();
	return (
		<div className={styles.exportRow}>
			<Typography className={styles.exportRowHeader} id={dateDropdownId}>
				{i18n.t("instructor.components.reports.exportModal.headers.filter")}
			</Typography>
			<CoreSelect<YearSelection>
				value={selectedYear}
				renderValue={(value) => getExportModalDropDownLabel(value)}
				onChange={(event) => onChange(event.target.value as YearSelection)}
				labelId={dateDropdownId}
				MenuProps={{
					PaperProps: {
						sx: {
							maxHeight: "200px",
						},
					},
				}}
			>
				<CoreMenuItem value={"current"}>
					<ListItemText>{getExportModalDropDownLabel("current")}</ListItemText>
				</CoreMenuItem>
				{availableYears.map((year) => {
					return (
						<CoreMenuItem key={year} value={year}>
							<ListItemText>{getExportModalDropDownLabel(year)}</ListItemText>
						</CoreMenuItem>
					);
				})}
			</CoreSelect>
		</div>
	);
}

function ExportEmailRow({
	emailError,
	onChange,
}: {
	emailError: string | undefined;
	onChange: (value: string) => void;
}) {
	const emailId = useId();

	return (
		<div className={`${styles.exportRow} ${emailError ? styles.error : ""}`}>
			<Typography id={emailId} className={styles.exportRowHeader}>
				{i18n.t("instructor.components.reports.exportModal.headers.email")}
			</Typography>

			<CoreTextField
				className={styles.emailTextField}
				placeholder={i18n.t("instructor.components.reports.exportModal.emailPlaceHolder")}
				inputProps={{ "aria-labelledby": emailId }}
				onChange={(e) => onChange(e.target.value)}
				error={emailError}
				InputProps={{ sx: { height: "35px" } }}
				required
			/>
		</div>
	);
}

const EXPORT_RADIO_OPTIONS: RadioOption[] = [
	{ value: "download", label: i18n.t("instructor.components.reports.exportModal.exportRadio.download") },
	{ value: "email", label: i18n.t("instructor.components.reports.exportModal.exportRadio.email") },
];

const FORMAT_RADIO_OPTIONS: RadioOption[] = [
	{ value: "json", label: i18n.t("instructor.components.reports.exportModal.formatRadio.json") },
	{ value: "csv", label: i18n.t("instructor.components.reports.exportModal.formatRadio.csv") },
	{ value: "excel", label: i18n.t("instructor.components.reports.exportModal.formatRadio.excel") },
];

function ExportReportRadioGroup({ onChange, selectedValue, type }: ExportReportRadioGroupProps) {
	const radioOptions = type === "export" ? EXPORT_RADIO_OPTIONS : FORMAT_RADIO_OPTIONS;
	const id = useId();
	const header =
		type === "export"
			? i18n.t("instructor.components.reports.exportModal.headers.export")
			: i18n.t("instructor.components.reports.exportModal.headers.format");

	return (
		<div className={styles.exportRow}>
			<Typography className={styles.exportRowHeader} id={id}>
				{header}
			</Typography>
			<CoreRadioGroup
				radioGroupAriaLabelledby={id}
				radioOptions={radioOptions}
				onChange={onChange}
				selectedValue={selectedValue}
				sx={{ marginLeft: "8px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr" }}
			/>
		</div>
	);
}

export function ExportReportButton({ reportInfo }: { reportInfo: ExportReportInfo }) {
	const [modalOpen, setModalOpen] = useState(false);

	return (
		<>
			<CoreButton
				onClick={() => setModalOpen(true)}
				startIcon={<ExportIcon />}
				className={styles.exportButton}
				aria-haspopup="dialog"
			>
				{i18n.t("instructor.components.reports.exportModal.buttons.openModal")}
			</CoreButton>
			{modalOpen && <ExportReportModal reportInfo={reportInfo} onClose={() => setModalOpen(false)} />}
		</>
	);
}

function ExportReportModal({ reportInfo, onClose }: { reportInfo: ExportReportInfo; onClose: () => void }) {
	const reportType = reportInfo.reportType;
	const instituteUuid = useContext(PortalControllerContext).portalContext?.instituteUuid;

	const [exportType, setExportType] = useState<ExportType>("download");
	const [formatType, setFormatType] = useState<FormatType>("excel");
	const [yearsWithData, setYearsWithData] = useState<number[]>([]);
	const [selectedYear, setSelectedYear] = useState<YearSelection>("current");
	const [email, setEmail] = useState<string>("");
	const [emailError, setEmailError] = useState<string | undefined>(getEmailError(email));
	const [pending, setPending] = useState<boolean>(false);

	const today = new Date();
	const filterStartDate =
		selectedYear === "current" ? reportInfo.institutionCourseReportInfo?.startDate : new Date(selectedYear, 0, 1);

	const filterEndDate =
		selectedYear === "current"
			? reportInfo.institutionCourseReportInfo?.endDate
			: selectedYear === today.getFullYear()
				? today
				: new Date(selectedYear, 11, 31);

	const description =
		filterStartDate && filterEndDate
			? i18n.t("instructor.components.reports.exportModal.descriptionWithDate")
			: i18n.t("instructor.components.reports.exportModal.descriptionNoDate");

	// Initialize date dropdown
	useEffect(() => {
		if (reportType === "assessment") return;
		const { id, verityFetch } = reportInfo.institutionCourseReportInfo;
		const loadYears = async () => {
			const years = await fetchDistinctYearsForReport({
				id: String(id),
				verityFetch: verityFetch,
				reportType: reportType,
			});
			if (!years) {
				coreToast.error(i18n.t("instructor.components.reports.exportModal.errors.historicalData"));
				return;
			}
			setYearsWithData(years);
		};
		void loadYears();
	}, [reportInfo.institutionCourseReportInfo, reportType]);

	async function handleExportReport() {
		setPending(true);

		// Get report data
		const data = await getReportData({
			reportType: reportType,
			reportInfo: reportInfo,
			filterStartDate: filterStartDate,
			filterEndDate: filterEndDate,
		});

		// Data will be undefined if there is an error fetching report results
		if (!data || !data["data"]) {
			coreToast.error(i18n.t("instructor.components.reports.exportModal.errors.fetchingData"));
			return;
		}

		const result = await exportReport({
			reportType: reportType,
			startDate: filterStartDate,
			endDate: filterEndDate,
			reportInfo: data["data"],
			name: data["subFileName"],
			formatType: formatType,
			subAccountName: reportInfo.institutionCourseReportInfo?.institutionInfo?.subAccountName,
			exportType: exportType,
			email: email,
		});

		if (result.success) {
			if (instituteUuid) {
				emitAnalytics(ReportsExportedEvent, {
					subject: instituteSubject(instituteUuid),
					payload: { data: { reportType, format: formatType, delivery: exportType } },
				});
			}
			coreToast.success(
				exportType === "download"
					? i18n.t("instructor.components.reports.exportModal.downloadSuccess")
					: i18n.t("instructor.components.reports.exportModal.emailSuccess"),
				{ autoClose: 1500 },
			);
		} else {
			coreToast.error(result.error);
		}

		onClose();
	}

	return (
		<CoreModal
			title={i18n.t("instructor.components.reports.exportModal.title")}
			open
			onClose={onClose}
			buttons={[
				{
					text: i18n.t("instructor.components.reports.exportModal.buttons.cancel"),
					color: "secondary",
					onClick: onClose,
				},
				{
					text: i18n.t("instructor.components.reports.exportModal.buttons.export"),
					color: "primary",
					onClick: handleExportReport,
					disabled: (exportType === "email" && emailError !== undefined) || pending,
				},
			]}
		>
			<>
				<Typography className={styles.exportReportDescription}>{description}</Typography>
				<ExportReportRadioGroup
					type="export"
					onChange={(_, value) => setExportType(value as ExportType)}
					selectedValue={exportType}
				/>
				{exportType === "email" && (
					<ExportEmailRow
						onChange={(value) => {
							setEmail(value);
							setEmailError(getEmailError(value));
						}}
						emailError={emailError}
					/>
				)}
				<ExportReportRadioGroup
					type="format"
					onChange={(_, value) => setFormatType(value as FormatType)}
					selectedValue={formatType}
				/>
				{filterStartDate && filterEndDate && (
					<ExportYearDropdown
						selectedYear={selectedYear}
						availableYears={yearsWithData}
						onChange={(value) => setSelectedYear(value)}
					/>
				)}
			</>
		</CoreModal>
	);
}

export function SelectedScopeChip({ name, onClear }: { name: string | undefined | null; onClear?: () => void }) {
	if (!name) return <></>;

	return (
		<CoreChip
			chipColor="#FFFFFF"
			style={{
				maxWidth: "300px",
				border: "1px solid #E5E7EB",
				height: "auto",
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", width: "100%", padding: "5px" }}>
				<Typography
					fontWeight={600}
					fontSize={"14px"}
					sx={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						flex: 1,
					}}
				>
					{name}
				</Typography>
				{onClear && (
					<IconButton
						sx={{
							flexShrink: 0,
							padding: "3px",
							borderRadius: "4px",
							marginLeft: "2px",
							"&:hover": {
								backgroundColor: "#ECECEC",
							},
						}}
						onClick={onClear}
					>
						<XIcon style={{ width: "16px", height: "16px" }} />
					</IconButton>
				)}
			</Box>
		</CoreChip>
	);
}

interface ReportHeaderProps {
	showRefreshButton?: boolean;
	setRefreshReportModalOpen?: (open: boolean) => void;
	lastUpdatedDate?: Date;
	startDate: Date;
	endDate: Date;
	setStartDate: (date: Date) => void;
	setEndDate: (date: Date) => void;
	averageIntegrityScore?: {
		total: number | null;
		change?: number | undefined;
	};
	type: ReportType;
	fixedDate?: boolean;
	switchButtons?: {
		subAccountSelectButton?: {
			text: string;
			onClick: () => void;
			body?: React.ReactNode;
		};
		courseSelectButton?: {
			text: string;
			onClick: () => void;
			body?: React.ReactNode;
		};
		assessmentSelectButton?: {
			text: string;
			onClick: () => void;
			body?: React.ReactNode;
		};
	};
	reportInfo?: ExportReportInfo;
	backButton?: {
		text: string;
		onBack: () => void;
	};
	/** Rendered at the start of the scope row, before the scope buttons and date range. */
	search?: React.ReactNode;
	/** Rendered in the top-right action area, before the export button. */
	headerActions?: React.ReactNode;
}

const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);

export function ReportHeader({
	showRefreshButton,
	setRefreshReportModalOpen,
	lastUpdatedDate,
	startDate,
	endDate,
	setStartDate,
	setEndDate,
	averageIntegrityScore,
	type,
	fixedDate,
	switchButtons,
	reportInfo,
	backButton,
	search,
	headerActions,
}: ReportHeaderProps) {
	// Keep the range valid when the start month moves past the end month.
	useEffect(() => {
		if (startDate > endDate) {
			setEndDate(endOfMonth(startDate));
		}
	}, [startDate, endDate]);

	const options: Intl.DateTimeFormatOptions = {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
	};

	return (
		<CoreCard style={{ padding: 0, display: "flex", flexDirection: "column" }}>
			<Box sx={{ padding: "16px", display: "flex", flexDirection: "column", gap: "16px" }}>
				{backButton && (
					<CoreBackButton
						text={backButton.text}
						onBack={backButton.onBack}
						sx={{
							alignSelf: "flex-start",
							"&:hover": { backgroundColor: "transparent" },
						}}
					/>
				)}
				<Grid container spacing={2} justifyContent="space-between">
					<Grid item xs={12} md={8} display="flex" flexDirection="column" gap="8px">
						{type !== "testTaker" && (
							<>
								<Box display="flex" alignItems="center" gap="16px" flexWrap="wrap">
									<Typography
										variant="h1"
										sx={{
											fontFamily: "Primary, sans-serif",
											fontWeight: 600,
											fontStyle: "normal",
											fontSize: "24px",
											lineHeight: "1.5",
											letterSpacing: "0px",
											color: "#262626",
										}}
									>
										{i18n.t(`instructor.components.reports.typeTitle.${type}`)}
									</Typography>

									{averageIntegrityScore !== undefined && (
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: "6px",
												border: "1px solid #E5E7EB",
												borderRadius: "6px",
												padding: "4px 10px",
											}}
										>
											<Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#6B6B6B" }}>
												{i18n.t("admin.components.institutionReportTSX.integrityScore")}
											</Typography>
											{typeof averageIntegrityScore.total === "number" ? (
												<Box display="flex" alignItems="baseline" gap="2px">
													<Typography
														sx={{
															fontSize: "18px",
															fontWeight: 700,
															color: getScoreColors(averageIntegrityScore.total).text,
														}}
													>
														{Math.round(averageIntegrityScore.total)}
													</Typography>
													<Typography
														sx={{
															fontSize: "13px",
															fontWeight: 500,
															color: getScoreColors(averageIntegrityScore.total).text,
														}}
													>
														/100
													</Typography>
												</Box>
											) : averageIntegrityScore.total === null ? (
												<Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#6B6B6B" }}>N/A</Typography>
											) : undefined}
										</Box>
									)}
								</Box>

								{type !== "usage" && (
									<Typography fontWeight={400} fontSize={"15px"} color={"#6b6b6b"}>
										{i18n.t(`instructor.components.reports.typeDesc.${type}`)}
									</Typography>
								)}
							</>
						)}
					</Grid>
					<Grid
						item
						xs={12}
						md={4}
						display="flex"
						flexWrap={{ xs: "wrap", md: "nowrap" }}
						justifyContent={{ xs: "flex-start", md: "flex-end" }}
						alignItems="flex-start"
						gap="12px"
					>
						<Box
							display="flex"
							alignItems="center"
							gap="12px"
							flexWrap={{ xs: "wrap", md: "nowrap" }}
							flexShrink={0}
							sx={{ mt: "2px", width: { xs: "100%", md: "auto" } }}
						>
							{type !== "assessment" &&
								type !== "usage" &&
								(lastUpdatedDate ? (
									<>
										<CoreChip
											chipColor="#FAFAFA"
											style={{
												height: "24px",
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												padding: "2px",
												gap: "12px",
												width: "fit-content",
												flexShrink: 0,
											}}
										>
											<div style={{ display: "flex", alignItems: "center", gap: "6px", whiteSpace: "nowrap" }}>
												<ClockIcon />
												<Typography
													sx={{
														fontWeight: 500,
														fontSize: "14px",
														lineHeight: "20px",
														letterSpacing: "0px",
														textAlign: "center",
														color: "#262626",
														whiteSpace: "nowrap",
													}}
												>
													{i18n.t("instructor.components.reports.lastUpdated")}
												</Typography>
												<Typography
													sx={{
														fontWeight: 500,
														fontSize: "14px",
														lineHeight: "20px",
														letterSpacing: "0px",
														textAlign: "center",
														color: "#404040",
														whiteSpace: "nowrap",
													}}
												>
													{lastUpdatedDate.getTime() === new Date(0).getTime()
														? i18n.t("instructor.components.reports.never")
														: lastUpdatedDate.toLocaleDateString(i18n.language, options)}
												</Typography>
											</div>
										</CoreChip>

										{showRefreshButton && setRefreshReportModalOpen && (
											<CoreChip
												chipColor="#ECF4F6"
												style={{
													height: "24px",
													padding: "2px",
													border: "1px solid #3C8C9D",
													cursor: "pointer",
													flexShrink: 0,
												}}
												onClick={() => setRefreshReportModalOpen(true)}
											>
												<div
													style={{
														display: "flex",
														flexDirection: "row",
														alignItems: "center",
														gap: "8px",
														whiteSpace: "nowrap",
													}}
												>
													<Refresh />
													<Typography
														sx={{
															fontWeight: 500,
															fontStyle: "normal",
															fontSize: "16px",
															lineHeight: "20px",
															letterSpacing: "0px",
															textAlign: "center",
															color: "#0B6F85",
														}}
													>
														{i18n.t("instructor.components.reports.refresh")}
													</Typography>
												</div>
											</CoreChip>
										)}
									</>
								) : (
									<Box display="flex" alignItems="center" flexShrink={0}>
										<Skeleton variant="text" width={320} height={22.5} />
									</Box>
								))}
							{headerActions}
							{reportInfo && (
								<Box flexShrink={0}>
									<ExportReportButton reportInfo={reportInfo} />
								</Box>
							)}
						</Box>
					</Grid>
				</Grid>
			</Box>

			<Box
				sx={{
					display: "flex",
					flexDirection: { xs: "column", md: "row" },
					justifyContent: "space-between",
					alignItems: { xs: "stretch", md: "center" },
					gap: { xs: "16px", md: "0" },
					backgroundColor: "#F9FAFB80",
					borderTop: "1px solid #EFEFEF",
					padding: "12px 16px",
				}}
			>
				<ScopeSelector
					switchButtons={switchButtons}
					fixedDate={fixedDate}
					startDate={startDate}
					endDate={endDate}
					setStartDate={setStartDate}
					setEndDate={setEndDate}
					type={type}
					search={search}
				/>
			</Box>
		</CoreCard>
	);
}

function ScopeSelector({
	switchButtons,
	fixedDate,
	startDate,
	endDate,
	setStartDate,
	setEndDate,
	type,
	search,
}: {
	switchButtons?: {
		subAccountSelectButton?: {
			text: string;
			onClick: () => void;
			body?: React.ReactNode;
		};
		courseSelectButton?: {
			text: string;
			onClick: () => void;
			body?: React.ReactNode;
		};
		assessmentSelectButton?: {
			text: string;
			onClick: () => void;
			body?: React.ReactNode;
		};
	};
	fixedDate?: boolean;
	startDate: Date;
	endDate: Date;
	setStartDate: (date: Date) => void;
	setEndDate: (date: Date) => void;
	type: ReportType;
	search?: React.ReactNode;
}) {
	// Both pickers are month-granular, so they are driven by the first of the month rather than
	// startDate/endDate directly: MUI validates disableFuture and minDate at day granularity, so a
	// value carrying a later day (endDate is the last day of its month) renders as a validation error.
	const startMonth = useMemo(() => startOfMonth(startDate), [startDate]);
	const endMonth = useMemo(() => startOfMonth(endDate), [endDate]);

	const startPicker = useMonthPicker(startMonth, (date) => setStartDate(startOfMonth(date)));
	const endPicker = useMonthPicker(endMonth, (date) => setEndDate(endOfMonth(date)));

	const buttonsConfig = [
		{
			id: "subAccount",
			config: switchButtons?.subAccountSelectButton,
			startIcon: <SelectSubAccount />,
			disabled: false,
		},
		{
			id: "course",
			config: switchButtons?.courseSelectButton,
			startIcon: <SelectCourse />,
			disabled: false,
		},
		{
			id: "assessment",
			config: switchButtons?.assessmentSelectButton,
			startIcon: <SelectAssessment />,
			disabled: false,
		},
	].filter((btn) => btn.config !== undefined);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: { xs: "column", md: "row" },
				flexWrap: "nowrap",
				alignItems: { xs: "flex-start", md: "center" },
				gap: "12px",
			}}
		>
			{search && <Box sx={{ width: { xs: "100%", md: "320px" }, flexShrink: 0 }}>{search}</Box>}
			{buttonsConfig.map((btn, index) => {
				const buttonElement = (
					<CoreIconButton
						variant="plain"
						aria-haspopup="dialog"
						onClick={btn.config!.onClick}
						disabled={btn.disabled}
						sx={{
							display: "flex",
							alignItems: "center",
							gap: "8px",
							backgroundColor: "#FFFFFF",
							border: "1px solid #E5E7EB",
							borderRadius: "4px",
							padding: "10px",
							color: "#344054",
							textTransform: "none",
							fontSize: "14px",
							fontWeight: 400,
							height: "36px",
							flexShrink: 0,
							opacity: btn.disabled ? 0.6 : 1,
							"&:hover": {
								backgroundColor: btn.disabled ? "#F9FAFB" : "#F9FAFB",
							},
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", color: "#667085" }}>{btn.startIcon}</Box>

						<Typography sx={{ fontSize: "inherit", fontWeight: "inherit", whiteSpace: "nowrap" }}>
							{btn.config!.text}
						</Typography>

						<Box sx={{ display: "flex", alignItems: "center", color: "#667085", ml: "4px" }}>
							<CaretDown style={{ width: "16px", height: "16px" }} />
						</Box>
					</CoreIconButton>
				);

				return (
					<React.Fragment key={index}>
						{btn.config!.body ? (
							<Box
								sx={{
									display: "flex",
									flexDirection: { xs: "column", md: "row" },
									alignItems: { xs: "flex-start", md: "center" },
									gap: "8px",
									position: "relative",
									flexShrink: 0,
									width: { xs: "100%", md: "auto" },
								}}
							>
								{buttonElement}
								{btn.config!.body}
							</Box>
						) : (
							buttonElement
						)}

						{index < buttonsConfig.length - 1 && (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									height: { xs: "auto", md: "36px" },
									color: "#98A2B3",
									transform: { xs: "rotate(90deg)", md: "none" },
									ml: { xs: "10px", md: "0" },
									flexShrink: 0,
								}}
							>
								<CaretRight style={{ width: "16px", height: "16px" }} />
							</Box>
						)}
					</React.Fragment>
				);
			})}
			{type !== "assessment" &&
				(fixedDate ? (
					<Box display="flex" alignItems="center" gap="6px" flexShrink={0} sx={{ whiteSpace: "nowrap" }}>
						<Typography fontWeight={400} fontSize={"15px"}>
							{i18n.t("instructor.components.reports.date")}
						</Typography>
						<Typography fontWeight={400} fontSize={"15px"} color={"#6B6B6B"}>
							{startDate.toLocaleString("default", {
								weekday: "long",
								year: "numeric",
								month: "long",
								day: "numeric",
							})}
						</Typography>
					</Box>
				) : (
					<Box
						sx={{
							display: "flex",
							alignItems: "stretch",
							border: "1px solid #949494",
							borderRadius: "8px",
							overflow: "hidden",
							width: "fit-content",
							height: "36px",
							flexShrink: 0,
							flexDirection: "row",
						}}
					>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								backgroundColor: "#F5F5F5",
								padding: "0 10px",
								borderRight: "1px solid #D0D0D0",
							}}
						>
							<CalendarIcon color="#404040" width={20} height={20} />
						</Box>

						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: "4px",
								flexDirection: "row",
								backgroundColor: "white",
								padding: "0px 8px",
								flexShrink: 0,
							}}
						>
							<CoreDatePicker
								hidePickerIcon={true}
								format="MMMM / yyyy"
								disableFuture
								views={["year", "month"]}
								{...startPicker}
								dialogAriaLabel={i18n.t("instructor.components.reports.ariaLabels.calendarStartDialog")}
								ariaLabel={i18n.t("instructor.components.reports.ariaLabels.calendarStartInput")}
								useButton
								slotProps={{
									textField: {
										sx: {
											height: "36px",
											width: "fit-content",
											whiteSpace: "nowrap",
											minWidth: "max-content",
											flexShrink: 0,

											"&&& input": {
												width: "100%",
												minWidth: "min-content",
												fontSize: "15px",
												textAlign: "center",
												cursor: "pointer",
												color: "#262626",
												whiteSpace: "nowrap",
												overflow: "hidden",
												textOverflow: "ellipsis",
											},
										},
									},
									popper: { placement: "bottom-start" },
								}}
							/>

							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									color: "#6B6B6B",
									transform: "none",
									flexShrink: 0,
								}}
							>
								<ArrowRight style={{ width: "20px", height: "auto" }} />
							</Box>

							<CoreDatePicker
								hidePickerIcon={true}
								format="MMMM / yyyy"
								disableFuture
								views={["year", "month"]}
								{...endPicker}
								dialogAriaLabel={i18n.t("instructor.components.reports.ariaLabels.calendarEndDialog")}
								ariaLabel={i18n.t("instructor.components.reports.ariaLabels.calendarEndInput")}
								minDate={startMonth}
								useButton
								slotProps={{
									textField: {
										sx: {
											height: "36px",
											width: "fit-content",
											whiteSpace: "nowrap",
											minWidth: "max-content",
											flexShrink: 0,

											"&&& input": {
												width: "100%",
												minWidth: "min-content",
												fontSize: "15px",
												textAlign: "center",
												cursor: "pointer",
												color: "#262626",
												whiteSpace: "nowrap",
												overflow: "hidden",
												textOverflow: "ellipsis",
											},
										},
									},
									popper: { placement: "bottom-start" },
								}}
							/>
						</Box>
					</Box>
				))}
		</Box>
	);
}

type AnalyticsTableCardProps<T extends object> = {
	title: string;
	description: string;
	searchLabel: string;
	searchValue: string;
	onSearch: (value: string) => void;
	totalResults: number;
	controlId: string;
	columns: CoreTableColumn<T>[];
	data: T[];
	loading: boolean;
	noResultsString: string;
	onRowClick?: (row: CoreTableRow<T>) => void;
	/** When true, renders title/description on their own row, followed by a divider, then the search field on its own row below — instead of title/description and search side by side. */
	stackedHeader?: boolean;
	fixedLayout?: boolean;
	rowProps?: (row: CoreTableRow<T>) => { sx?: object; ref?: React.Ref<HTMLTableRowElement> };
	allowCellOverflowVisible?: boolean;
	/** Column key to sort by before the user picks a sort themselves. */
	initialSortKey?: React.Key;
	/** @default "asc" */
	initialSortOrder?: "asc" | "desc";
};

// region Report Tables
function AnalyticsTableCard<T extends object>({
	title,
	description,
	searchLabel,
	searchValue,
	onSearch,
	totalResults,
	controlId,
	columns,
	data,
	loading,
	noResultsString,
	onRowClick,
	stackedHeader,
	fixedLayout,
	rowProps,
	allowCellOverflowVisible,
	initialSortKey,
	initialSortOrder,
}: AnalyticsTableCardProps<T>) {
	return (
		<CoreCard style={{ padding: 0 }}>
			{stackedHeader ? (
				<>
					<Box display="flex" flexDirection="column" padding="13px" sx={{ minHeight: "76px" }}>
						<Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#262626" }}>{title}</Typography>
						<Typography sx={{ fontSize: "13px", color: "#6B6B6B" }}>{description}</Typography>
					</Box>

					<Box sx={{ borderTop: "1px solid #EFEFEF" }} />

					<Box padding="13px">
						<CoreSearchField
							label={searchLabel}
							onSearch={onSearch}
							totalResults={totalResults}
							ariaControls={controlId}
							sx={{ width: "100%" }}
							value={searchValue}
						/>
					</Box>
				</>
			) : (
				<Box
					display="flex"
					flexDirection={{ xs: "column", md: "row" }}
					justifyContent="space-between"
					alignItems={{ xs: "flex-start", md: "flex-start" }}
					gap={{ xs: "8px", md: "16px" }}
					padding="13px"
				>
					<Box display="flex" flexDirection="column">
						<Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#262626" }}>{title}</Typography>
						<Typography sx={{ fontSize: "13px", color: "#6B6B6B" }}>{description}</Typography>
					</Box>

					<CoreSearchField
						label={searchLabel}
						onSearch={onSearch}
						totalResults={totalResults}
						ariaControls={controlId}
						sx={{ flexShrink: 0 }}
						value={searchValue}
					/>
				</Box>
			)}
			<CoreTable
				variant="settings"
				id={controlId}
				loading={loading}
				columns={columns}
				data={data}
				paginationSize="large"
				rowsPerPage={5}
				allowUIOverrides
				boldHeader={false}
				firstRowRounded={false}
				lastRowRounded={false}
				hideOuterBorder={{ left: true, right: true }}
				padding="20px max(22px, min(30px, 1.8vw))"
				noResultsString={noResultsString}
				onRowClick={onRowClick}
				fixedLayout={fixedLayout}
				rowProps={rowProps}
				allowCellOverflowVisible={allowCellOverflowVisible}
				initialSortKey={initialSortKey}
				initialSortOrder={initialSortOrder}
				sortRequired
				rowHeight={75}
			/>
		</CoreCard>
	);
}

function ClickableNameCell({ name, bold }: { name: string; bold?: boolean }) {
	return (
		<Tooltip title={name}>
			<Box
				component="span"
				sx={{
					color: "#0B6F85",
					fontWeight: bold ? 700 : 500,
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
					display: "block",
					"&:hover": {
						textDecoration: "underline",
					},
				}}
			>
				{name}
			</Box>
		</Tooltip>
	);
}

function RowSelectableIntegrityScoreCell({
	integrityScore,
	onActivate,
}: {
	integrityScore: number | null | undefined;
	onActivate?: () => void;
}) {
	return (
		<Box sx={{ position: "relative", width: "100%", height: "100%" }}>
			<Box
				className={ROW_SELECT_HIDE_CLASS}
				sx={{
					position: "absolute",
					top: 0,
					bottom: 0,
					left: 0,
					display: "flex",
					alignItems: "center",
					transition: `opacity ${ROW_SELECT_TRANSITION_MS}ms ease-out`,
				}}
			>
				<IntegrityLevelBadge integrityLevel={integrityScore ?? null} />
			</Box>
			{onActivate && <RowSelectOverlay onActivate={onActivate} />}
		</Box>
	);
}

const ROW_SELECT_TRANSITION_MS = 180;
const ROW_SELECT_REVEAL_CLASS = "row-select-reveal";
/** Class on the cell's normal content (e.g. a badge) that fades out when the button reveals. */
const ROW_SELECT_HIDE_CLASS = "row-select-hide";

const rowSelectHoverSx = {
	[`&:hover .${ROW_SELECT_REVEAL_CLASS}`]: {
		opacity: 1,
		transform: "translateX(0)",
		pointerEvents: "auto",
	},
	[`&:hover .${ROW_SELECT_HIDE_CLASS}`]: {
		opacity: 0,
	},
};

function RowSelectOverlay({ onActivate }: { onActivate: () => void }) {
	return (
		<Box
			className={ROW_SELECT_REVEAL_CLASS}
			sx={{
				position: "absolute",
				top: 0,
				bottom: 0,
				right: "0px",
				display: "flex",
				alignItems: "center",
				gap: "8px",
				opacity: 0,
				transform: "translateX(24px)",
				pointerEvents: "none",
				willChange: "opacity, transform",
				backfaceVisibility: "hidden",
				transition: `opacity ${ROW_SELECT_TRANSITION_MS}ms ease-out, transform ${ROW_SELECT_TRANSITION_MS}ms cubic-bezier(0.22,0.8,0.3,1)`,
			}}
		>
			<Box
				tabIndex={-1}
				onClick={(e: React.MouseEvent) => {
					e.stopPropagation();
					onActivate();
				}}
			>
				{i18n.t("instructor.components.reports.usage.viewReport")}
			</Box>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
				}}
				aria-hidden="true"
			>
				<CaretRight style={{ width: "14px", height: "14px" }} />
			</Box>
		</Box>
	);
}

function CourseTable({ data }: { data?: CourseTableEntry[] }) {
	const { changePortalContext, navigateToPage } = usePortalStateContext();
	const [courseSearch, setCourseSearch] = useState<string>();
	const controlId = useId();

	const filteredData = useMemo(() => {
		return (
			data?.filter(
				(s) =>
					s.averageIntegrityScore !== null &&
					(!courseSearch ||
						courseSearch === "" ||
						s.lmsCourseId.toLowerCase().includes(courseSearch.toLowerCase()) ||
						s.courseName.toLowerCase().includes(courseSearch.toLowerCase())),
			) ?? []
		);
	}, [data, courseSearch]);

	const onRowClick = async (row: CoreTableRow<CourseTableEntry>) => {
		await changePortalContext({ courseId: row.data.courseId, assessmentId: null });
		navigateToPage("courseReport");
	};

	const columns: CoreTableColumn<CourseTableEntry>[] = [
		{
			header: i18n.t("instructor.components.reports.integrity.institutionView.courseAnalytics.courseName"),
			key: "courseName",
			cell: (row) => <ClickableNameCell name={row.data.courseName} />,
			align: "left",
			truncateString: true,
			descendingComparator: (a, b) => b.courseName.localeCompare(a.courseName),
			width: "40%",
		},
		{
			header: i18n.t("instructor.components.reports.integrity.institutionView.courseAnalytics.lmsCourseId"),
			key: "lmsCourseId",
			cell: (row) => (
				<Tooltip title={row.data.lmsCourseId}>
					<div style={{ color: "#667085" }}>{row.data.lmsCourseId}</div>
				</Tooltip>
			),
			align: "left",
			truncateString: true,
			descendingComparator: (a, b) => b.lmsCourseId.localeCompare(a.lmsCourseId),
			width: "15%",
		},
		{
			header: i18n.t("instructor.components.reports.integrity.institutionView.courseAnalytics.numberOfStudents"),
			key: "studentCount",
			cell: (row) => <div style={{ color: "#667085" }}>{row.data.studentCount}</div>,
			align: "left",
			descendingComparator: (a, b) => b.studentCount - a.studentCount,
			width: "15%",
		},
		{
			header: i18n.t("instructor.components.reports.integrity.institutionView.courseAnalytics.numberOfAssessments"),
			key: "numberOfAssessments",
			cell: (row) => <div style={{ color: "#667085" }}>{row.data.assessmentCount}</div>,
			align: "left",
			descendingComparator: (a, b) => b.assessmentCount - a.assessmentCount,
			width: "15%",
		},
		{
			header: i18n.t("instructor.components.reports.integrity.institutionView.courseAnalytics.integrityScore"),
			key: "averageIntegrityScore",
			cell: (row) => (
				<RowSelectableIntegrityScoreCell
					integrityScore={row.data.averageIntegrityScore}
					onActivate={() => onRowClick(row)}
				/>
			),
			align: "left",
			descendingComparator: (a, b) => (b.averageIntegrityScore ?? 0) - (a.averageIntegrityScore ?? 0),
			width: "25%",
			tableCellContentWidth: "100%",
		},
	];

	const isSmallScreen = useMediaQuery("(max-width: 1200px)");
	return (
		<AnalyticsTableCard
			fixedLayout={!isSmallScreen}
			title={i18n.t("instructor.components.reports.integrity.institutionView.courseAnalytics.title")}
			description={i18n.t("instructor.components.reports.integrity.institutionView.courseAnalytics.description")}
			searchLabel={i18n.t("admin.components.institutionReportTSX.searchByCourseName")}
			searchValue={courseSearch ?? ""}
			onSearch={setCourseSearch}
			totalResults={filteredData.length}
			controlId={controlId}
			columns={columns}
			data={filteredData}
			loading={!data}
			noResultsString={i18n.t("admin.components.institutionReportTSX.noResultTxt")}
			onRowClick={onRowClick}
			rowProps={() => ({ sx: rowSelectHoverSx })}
			allowCellOverflowVisible
			initialSortKey="numberOfAssessments"
			initialSortOrder="desc"
		/>
	);
}

export function CourseTableContainer({ data }: { data?: CourseTableEntry[] }) {
	return <CourseTable data={data} />;
}

function AssessmentTable({ data, hideViewReport }: { data?: AssessmentTableEntry[]; hideViewReport?: boolean }) {
	const { changePortalContext, navigateToPage } = usePortalStateContext();
	const [assessmentSearch, setAssessmentSearch] = useState<string>();
	const controlId = useId();

	const filteredData =
		data?.filter(
			(s) =>
				s.averageIntegrityScore !== null &&
				(!assessmentSearch ||
					assessmentSearch === "" ||
					s.quizName.toLowerCase().includes(assessmentSearch.toLowerCase())),
		) ?? [];

	const onRowClick = hideViewReport
		? undefined
		: async (row: CoreTableRow<AssessmentTableEntry>) => {
				await changePortalContext({ assessmentId: row.data.quizId });
				navigateToPage("assessmentReport", { assessmentReportBackTo: "courseReport" });
			};

	const columns: CoreTableColumn<AssessmentTableEntry>[] = [
		{
			header: i18n.t("instructor.components.reports.integrity.courseView.assessmentAnalytics.assessment"),
			key: "assessmentName",
			cell: (row) => <ClickableNameCell name={row.data.quizName} />,
			truncateString: true,
			align: "left",
			width: "35%",
			descendingComparator: (a, b) => b.quizName.localeCompare(a.quizName),
		},
		{
			header: i18n.t("instructor.components.reports.integrity.courseView.assessmentAnalytics.testTakers"),
			key: "studentsProctored",
			cell: (row) => row.data.studentCount,
			align: "left",
			width: "20%",
			descendingComparator: (a, b) => b.studentCount - a.studentCount,
		},
		{
			header: i18n.t("instructor.components.reports.integrity.courseView.assessmentAnalytics.flaggedEvents"),
			key: "flaggedEvents",
			cell: (row) => row.data.flaggedEvents,
			align: "left",
			width: "20%",
			descendingComparator: (a, b) => b.flaggedEvents - a.flaggedEvents,
		},
		{
			header: i18n.t("instructor.components.reports.integrity.courseView.assessmentAnalytics.integrityScore"),
			key: "integrityScore",
			cell: (row) => (
				<RowSelectableIntegrityScoreCell
					integrityScore={row.data.averageIntegrityScore}
					onActivate={() => onRowClick?.(row)}
				/>
			),
			align: "left",
			width: "25%",
			tableCellContentWidth: "100%",
			descendingComparator: (a, b) => (b.averageIntegrityScore ?? 0) - (a.averageIntegrityScore ?? 0),
		},
	];

	const isSmallScreen = useMediaQuery("(max-width: 600px)");
	return (
		<AnalyticsTableCard
			stackedHeader
			fixedLayout={!isSmallScreen}
			title={i18n.t("instructor.components.reports.integrity.courseView.assessmentAnalytics.title")}
			description={i18n.t("instructor.components.reports.course.assessmentTableDescription")}
			searchLabel={i18n.t(
				"instructor.components.reports.integrity.courseView.assessmentAnalytics.searchByAssessmentName",
			)}
			searchValue={assessmentSearch ?? ""}
			onSearch={setAssessmentSearch}
			totalResults={filteredData.length}
			controlId={controlId}
			columns={columns}
			data={filteredData}
			rowProps={hideViewReport ? undefined : () => ({ sx: rowSelectHoverSx })}
			loading={!data}
			noResultsString={i18n.t("instructor.components.reports.integrity.courseView.assessmentAnalytics.noAssessment")}
			onRowClick={onRowClick}
			allowCellOverflowVisible
			initialSortKey="studentsProctored"
			initialSortOrder="desc"
		/>
	);
}

export function AssessmentTableContainer({
	data,
	hideViewReport,
}: {
	data?: AssessmentTableEntry[];
	hideViewReport?: boolean;
}) {
	return <AssessmentTable data={data} hideViewReport={hideViewReport} />;
}

type TestTakerTableProps = {
	data?: TestTakerTableEntry[];
	instituteId?: number;
	courseId?: number;
	assessmentId?: number;
} & Pick<
	ReportOverviewProps,
	"isSetupGuideWorkflow" | "isSetupGuideFastTrack" | "lms" | "onViewResults" | "onProctoringDataChanged"
>;

function TestTakerTable({
	data,
	instituteId,
	courseId,
	assessmentId,
	isSetupGuideWorkflow,
	isSetupGuideFastTrack,
	lms,
	onViewResults,
	onProctoringDataChanged,
}: TestTakerTableProps) {
	const [searchQuery, setSearchQuery] = useState<string>();
	const [selectedUserId, setSelectedUserId] = useState<number>();
	const [fetchedCourseId, setFetchedCourseId] = useState<number>();
	const controlId = useId();

	useEffect(() => {
		setSelectedUserId(undefined);
	}, [instituteId, courseId, assessmentId]);

	useEffect(() => {
		let cancelled = false;
		setFetchedCourseId(undefined);
		if (courseId !== undefined || assessmentId === undefined)
			return () => {
				cancelled = true;
			};
		apiClient.assessment
			.getAssessmentByAssessmentId(assessmentId)
			.then((assessment) => {
				if (!cancelled) setFetchedCourseId(assessment?.courseId);
			})
			.catch((err) => {
				if (!cancelled) console.error(err);
			});
		return () => {
			cancelled = true;
		};
	}, [courseId, assessmentId]);

	const effectiveCourseId = courseId ?? fetchedCourseId;

	const studentList = useMemo(
		() =>
			(data ?? []).map((row) => ({
				id: row.userId,
				name: row.fullName ?? i18n.t("result.components.resultsPage.testResultsWrapperTSX.unknownStudent"),
				severityLevel: row.highestFlagLevel,
			})),
		[data],
	);

	const filteredData =
		data?.filter(
			(s) =>
				s.averageIntegrityScore !== null &&
				(!searchQuery ||
					searchQuery === "" ||
					(s.fullName && s.fullName.toLowerCase().includes(searchQuery.toLowerCase()))),
		) ?? [];

	const onRowClick = (row: CoreTableRow<TestTakerTableEntry>) => {
		setSelectedUserId(row.data.userId);
		onViewResults?.();
		setHasHighlightRow(false);
	};

	// Highlights the first row for the "View Results" step of the mandatory setup guide.
	const setupGuideHighlight = useMemo(() => {
		if (!isSetupGuideWorkflow || !lms) return undefined;
		return (
			getSetupStepDetails(getSetupTextByLMS(lms, "view"), "viewResultVerity", {
				skipStepsBeforeId: isSetupGuideFastTrack ? "viewResultVerity" : undefined,
			}) ?? undefined
		);
	}, [isSetupGuideWorkflow, isSetupGuideFastTrack, lms]);

	const [hasHighlightRow, setHasHighlightRow] = useState(false);
	const { ref: highlightRowRef, scrollIntoView: scrollHighlightRowIntoView } = useScrollIntoView<HTMLTableRowElement>();

	useEffect(() => {
		if (hasHighlightRow) scrollHighlightRowIntoView();
	}, [hasHighlightRow, scrollHighlightRowIntoView]);

	const setHighlightRowRef = useCallback(
		(node: HTMLTableRowElement | null) => {
			highlightRowRef.current = node;
			setHasHighlightRow(Boolean(node));
		},
		[highlightRowRef],
	);

	const columns: CoreTableColumn<TestTakerTableEntry>[] = [
		{
			header: i18n.t("instructor.components.reports.integrity.assessmentView.testTakerAnalytics.testTaker"),
			key: "testTakerName",
			cell: (row) => <ClickableNameCell name={row.data.fullName ?? ""} />,
			truncateString: true,
			align: "left",
			width: "25%",
			descendingComparator: (a, b) => (a.fullName ?? "").localeCompare(b.fullName ?? ""),
		},
		{
			header: i18n.t("instructor.components.reports.integrity.assessmentView.testTakerAnalytics.attempts"),
			key: "attempts",
			cell: (row) => row.data.attempts,
			align: "left",
			width: "15%",
			descendingComparator: (a, b) => a.attempts - b.attempts,
		},
		{
			header: i18n.t("instructor.components.reports.integrity.assessmentView.testTakerAnalytics.status"),
			key: "status",
			cell: (row) => row.data.status ?? "N/A",
			align: "left",
			width: "20%",
			descendingComparator: (a, b) => (a.status ?? "").localeCompare(b.status ?? ""),
		},
		{
			header: i18n.t("instructor.components.reports.integrity.assessmentView.testTakerAnalytics.startDate"),
			key: "startDate",
			cell: (row) => {
				if (!row.data.startDate) return "N/A";
				return new Date(row.data.startDate).toLocaleDateString("en-US", {
					month: "short",
					day: "numeric",
					year: "numeric",
				});
			},
			align: "left",
			width: "20%",
			descendingComparator: (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
		},
		{
			header: i18n.t("instructor.components.reports.integrity.assessmentView.testTakerAnalytics.integrityScore"),
			key: "averageIntegrityScore",
			cell: (row) => (
				<RowSelectableIntegrityScoreCell
					integrityScore={row.data.averageIntegrityScore}
					onActivate={() => onRowClick(row)}
				/>
			),
			align: "left",
			width: "20%",
			tableCellContentWidth: "100%",
			descendingComparator: (a, b) => (b.averageIntegrityScore ?? 0) - (a.averageIntegrityScore ?? 0),
		},
	];

	return (
		<>
			<AnalyticsTableCard
				title={i18n.t("instructor.components.reports.integrity.assessmentView.testTakerAnalytics.title")}
				description={i18n.t("instructor.components.reports.integrity.assessmentView.testTakerAnalytics.description")}
				searchLabel={i18n.t(
					"instructor.components.reports.integrity.assessmentView.testTakerAnalytics.searchByTestTakerName",
				)}
				searchValue={searchQuery ?? ""}
				onSearch={setSearchQuery}
				totalResults={filteredData.length}
				controlId={controlId}
				columns={columns}
				data={filteredData}
				loading={!data}
				noResultsString={i18n.t(
					"instructor.components.reports.integrity.assessmentView.testTakerAnalytics.noTestTaker",
				)}
				onRowClick={onRowClick}
				rowProps={(row) => ({
					sx: rowSelectHoverSx,
					...(setupGuideHighlight && row.absoluteIndex === 0 ? { ref: setHighlightRowRef } : {}),
				})}
				allowCellOverflowVisible
				initialSortKey="averageIntegrityScore"
				initialSortOrder="asc"
			/>
			{setupGuideHighlight && hasHighlightRow && (
				<QuickSetupHighlight
					targetRef={highlightRowRef}
					title={setupGuideHighlight.title}
					description={setupGuideHighlight.description}
					chipText={setupGuideHighlight.chipText}
					align="center"
					position="top"
					zIndex={SETUP_GUIDE_ATTRIBUTES.Z_INDEX.HIGHLIGHT_PORTAL}
				/>
			)}
			{selectedUserId !== undefined &&
				instituteId !== undefined &&
				effectiveCourseId !== undefined &&
				assessmentId !== undefined && (
					<TestTakerResultModal
						// Deliberately not keyed on selectedUserId: switching test taker inside the overlay must not
						// remount it, or the assessment/session-list load below would re-run on every switch.
						key={`${instituteId}-${effectiveCourseId}-${assessmentId}`}
						instituteId={instituteId}
						courseId={effectiveCourseId}
						assessmentId={assessmentId}
						selectedUserId={selectedUserId}
						setSelectedUserId={setSelectedUserId}
						studentList={studentList}
						onAttemptDeleted={onProctoringDataChanged}
					/>
				)}
		</>
	);
}

export function TestTakerTableContainer(props: TestTakerTableProps) {
	return <TestTakerTable {...props} />;
}

export function FlagAnalyticsTable({ data }: { data?: FlagTypeBreakdown[] }) {
	const controlId = useId();
	const [flagSearch, setFlagSearch] = useState<string>();

	const flagAnalyticsData = useMemo(() => {
		if (!data) return [];

		return data.map((breakdown) => {
			const anomalyKey = breakdown.type as AnomalyType;
			const title = ANOMALY_TO_EVENT_NAME[anomalyKey] ?? breakdown.type;
			const description = ANOMALY_TO_DESCRIPTION[anomalyKey] ?? "";

			return { id: breakdown.type, title, description, ...breakdown };
		});
	}, [data]);

	const filteredData = useMemo(() => {
		return flagAnalyticsData.filter(
			(row) => !flagSearch || flagSearch === "" || row.title.toLowerCase().includes(flagSearch.toLowerCase()),
		);
	}, [flagAnalyticsData, flagSearch]);

	const columns: CoreTableColumn<(typeof flagAnalyticsData)[0]>[] = [
		{
			header: i18n.t("instructor.components.reports.integrity.flagAnalytics.flagType"),
			key: "flagType",
			cell: (row) => (
				<Box display="flex" flexDirection="column" gap="2px">
					<div style={{ fontWeight: 600, color: "#2B2F32" }}>{row.data.title}</div>
					<Tooltip title={row.data.description}>
						<div
							style={{
								fontSize: "13px",
								color: "#667085",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
								maxWidth: "420px",
							}}
						>
							{row.data.description}
						</div>
					</Tooltip>
				</Box>
			),
			align: "left",
			width: "35%",
			descendingComparator: (a, b) => a.title.localeCompare(b.title),
		},
		{
			header: i18n.t("instructor.components.reports.integrity.flagAnalytics.occurrence"),
			key: "occurrence",
			cell: (row) => row.data.occurrences.toLocaleString(),
			align: "left",
			width: "15%",
			descendingComparator: (a, b) => b.occurrences - a.occurrences,
		},
		{
			header: i18n.t("instructor.components.reports.integrity.flagAnalytics.severity"),
			key: "severity",
			cell: (row) => <FlagLevelBadge flagLevel={row.data.severityLevel} />,
			align: "left",
			width: "15%",
			descendingComparator: (a, b) => b.severityLevel - a.severityLevel,
		},
	];

	return (
		<AnalyticsTableCard
			title={i18n.t("instructor.components.reports.integrity.flagAnalytics.title")}
			description={i18n.t("instructor.components.reports.integrity.flagAnalytics.description")}
			searchLabel={i18n.t("instructor.components.reports.integrity.flagAnalytics.searchFlagType")}
			searchValue={flagSearch ?? ""}
			onSearch={setFlagSearch}
			totalResults={filteredData.length}
			controlId={controlId}
			columns={columns}
			data={filteredData}
			loading={!data}
			noResultsString={i18n.t("instructor.components.reports.integrity.flagAnalytics.noResults")}
		/>
	);
}

export function FlagAnalyticsTableContainer({ data }: { data?: FlagTypeBreakdown[] }) {
	return <FlagAnalyticsTable data={data} />;
}

function ProctoringSessionsTable({
	data,
	instituteId,
	courseId,
	onProctoringDataChanged,
}: {
	data?: {
		userId: number;
		fullName: string;
		assessmentId: number;
		assessmentName: string;
		integrityScore: number;
		attemptNumber: number;
		totalAttempts: number;
		proctoringSessionId: string;
		severityLevel: number;
	}[];
	instituteId?: number;
	courseId?: number;
	onProctoringDataChanged?: () => void;
}) {
	const [search, setSearch] = useState<string>();
	const [selectedRow, setSelectedRow] = useState<{
		userId: number;
		assessmentId: number;
		proctoringSessionId?: string;
	}>();
	const controlId = useId();

	const filteredData = useMemo(() => {
		return (
			data?.filter(
				(row) =>
					!search ||
					search === "" ||
					row.fullName.toLowerCase().includes(search.toLowerCase()) ||
					row.assessmentName.toLowerCase().includes(search.toLowerCase()),
			) ?? []
		);
	}, [data, search]);

	const onRowClick = (row: CoreTableRow<(typeof filteredData)[0]>) => {
		setSelectedRow({
			userId: row.data.userId,
			assessmentId: row.data.assessmentId,
			proctoringSessionId: row.data.proctoringSessionId,
		});
	};

	const columns: CoreTableColumn<(typeof filteredData)[0]>[] = [
		{
			header: i18n.t("instructor.components.reports.integrity.courseView.proctoringSessions.testTaker"),
			key: "testTaker",
			cell: (row) => <ClickableNameCell name={row.data.fullName} />,
			align: "left",
			truncateString: true,
			width: "30%",
			descendingComparator: (a, b) => b.fullName.localeCompare(a.fullName),
		},
		{
			header: i18n.t("instructor.components.reports.integrity.courseView.proctoringSessions.assessment"),
			key: "assessment",
			cell: (row) => (
				<Tooltip title={row.data.assessmentName}>
					<span
						style={{
							color: "#262626",
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							display: "block",
						}}
					>
						{row.data.assessmentName}
					</span>
				</Tooltip>
			),
			align: "left",
			truncateString: true,
			width: "30%",
			descendingComparator: (a, b) => b.assessmentName.localeCompare(a.assessmentName),
		},
		{
			header: i18n.t("instructor.components.reports.integrity.courseView.proctoringSessions.attemptNumber"),
			key: "attemptNumber",
			cell: (row) => `${row.data.attemptNumber}/${row.data.totalAttempts}`,
			align: "left",
			width: "15%",
			descendingComparator: (a, b) => b.attemptNumber - a.attemptNumber,
		},
		{
			header: i18n.t("instructor.components.reports.integrity.courseView.proctoringSessions.integrityScore"),
			key: "integrityScore",
			cell: (row) => (
				<RowSelectableIntegrityScoreCell integrityScore={row.data.integrityScore} onActivate={() => onRowClick(row)} />
			),
			align: "left",
			width: "25%",
			tableCellContentWidth: "100%",
			descendingComparator: (a, b) => a.integrityScore - b.integrityScore,
		},
	];

	const studentList = useMemo(
		() =>
			(data ?? [])
				.filter((row) => row.assessmentId === selectedRow?.assessmentId)
				.filter((row, index, rows) => rows.findIndex((other) => other.userId === row.userId) === index)
				.map((row) => ({ id: row.userId, name: row.fullName, severityLevel: row.severityLevel })),
		[data, selectedRow?.assessmentId],
	);

	const isSmallScreen = useMediaQuery("(max-width: 600px)");
	return (
		<>
			<AnalyticsTableCard
				stackedHeader
				fixedLayout={!isSmallScreen}
				allowCellOverflowVisible
				title={i18n.t("instructor.components.reports.integrity.courseView.proctoringSessions.title")}
				description={i18n.t("instructor.components.reports.integrity.courseView.proctoringSessions.description", {
					count: data?.length ?? 0,
				})}
				searchLabel={i18n.t("instructor.components.reports.integrity.courseView.proctoringSessions.searchPlaceholder")}
				searchValue={search ?? ""}
				onSearch={setSearch}
				totalResults={filteredData.length}
				controlId={controlId}
				columns={columns}
				data={filteredData}
				loading={data ? false : true}
				noResultsString={i18n.t("instructor.components.reports.integrity.courseView.proctoringSessions.noResults")}
				onRowClick={onRowClick}
				rowProps={() => ({ sx: rowSelectHoverSx })}
			/>
			{selectedRow !== undefined && instituteId !== undefined && courseId !== undefined && (
				<TestTakerResultModal
					key={`${instituteId}-${courseId}-${selectedRow.assessmentId}`}
					instituteId={instituteId}
					courseId={courseId}
					assessmentId={selectedRow.assessmentId}
					selectedUserId={selectedRow.userId}
					// Switching test taker inside the drawer has no known session for the new user, so fall
					// back to that user's default attempt rather than keeping the previous session's uuid.
					setSelectedUserId={(userId) =>
						setSelectedRow(userId === undefined ? undefined : { userId, assessmentId: selectedRow.assessmentId })
					}
					studentList={studentList}
					initialProctoringSessionId={selectedRow.proctoringSessionId}
					onAttemptDeleted={onProctoringDataChanged}
				/>
			)}
		</>
	);
}

export function ProctoringSessionsContainer({
	data,
	instituteId,
	courseId,
	onProctoringDataChanged,
}: {
	data?: {
		userId: number;
		fullName: string;
		assessmentId: number;
		assessmentName: string;
		integrityScore: number;
		attemptNumber: number;
		totalAttempts: number;
		proctoringSessionId: string;
		severityLevel: number;
	}[];
	instituteId?: number;
	courseId?: number;
	onProctoringDataChanged?: () => void;
}) {
	return (
		<ProctoringSessionsTable
			data={data}
			instituteId={instituteId}
			courseId={courseId}
			onProctoringDataChanged={onProctoringDataChanged}
		/>
	);
}
// endregion
